/**
 * Lectura de artículos (blog + Atlas) — Analytics → Producto.
 * RPC: get_product_article_analytics(p_days)
 */

import { useQuery } from '@tanstack/react-query';
import { dbClient } from '@/services/db-client';

export interface ArticleOverview {
    blog_readers: number;
    blog_reads: number;
    blog_pct: number | null;
    atlas_readers: number;
    atlas_reads: number;
    atlas_pct: number | null;
    dwell_p50_sec: number | null;
    dwell_avg_sec: number | null;
    hop_readers: number;
    hop_pct: number | null;
    avg_articles_per_reader: number | null;
}

export interface ArticleSurfaceRow {
    key: string;
    readers: number;
    reads: number;
    dwell_p50_sec: number | null;
    dwell_avg_sec: number | null;
    hop_pct: number | null;
}

export interface ArticleChannelRow {
    key: string;
    readers: number;
    reads: number;
}

export interface ArticleTypeRow {
    surface: string;
    key: string;
    label: string;
    readers: number;
    reads: number;
    reads_pct: number | null;
}

export interface ArticleTopRow {
    surface: string;
    slug: string;
    title: string;
    type_label: string;
    readers: number;
    reads: number;
    dwell_p50_sec: number | null;
}

export interface ProductArticleAnalytics {
    period_days: number;
    active_users: number;
    overview: ArticleOverview;
    by_surface: ArticleSurfaceRow[];
    by_channel: ArticleChannelRow[];
    by_type: ArticleTypeRow[];
    top_articles: ArticleTopRow[];
}

function asNumber(v: unknown, fallback = 0): number {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '') {
        const n = Number(v);
        if (Number.isFinite(n)) return n;
    }
    return fallback;
}

function asNullableNumber(v: unknown): number | null {
    if (v == null) return null;
    const n = asNumber(v, Number.NaN);
    return Number.isFinite(n) ? n : null;
}

function parseOverview(raw: unknown): ArticleOverview {
    const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    return {
        blog_readers: asNumber(o.blog_readers),
        blog_reads: asNumber(o.blog_reads),
        blog_pct: asNullableNumber(o.blog_pct),
        atlas_readers: asNumber(o.atlas_readers),
        atlas_reads: asNumber(o.atlas_reads),
        atlas_pct: asNullableNumber(o.atlas_pct),
        dwell_p50_sec: asNullableNumber(o.dwell_p50_sec),
        dwell_avg_sec: asNullableNumber(o.dwell_avg_sec),
        hop_readers: asNumber(o.hop_readers),
        hop_pct: asNullableNumber(o.hop_pct),
        avg_articles_per_reader: asNullableNumber(o.avg_articles_per_reader),
    };
}

export function parseProductArticleAnalytics(raw: unknown): ProductArticleAnalytics | null {
    if (raw == null) return null;
    let payload: unknown = raw;
    if (typeof payload === 'string') {
        try {
            payload = JSON.parse(payload);
        } catch {
            return null;
        }
    }
    if (Array.isArray(payload)) payload = payload[0];
    if (!payload || typeof payload !== 'object') return null;
    const o = payload as Record<string, unknown>;
    const wrapped = o.get_product_article_analytics;
    if (wrapped && typeof wrapped === 'object') payload = wrapped;
    const p = payload as Record<string, unknown>;
    if (p.period_days == null || p.overview == null) return null;

    return {
        period_days: asNumber(p.period_days, 30),
        active_users: asNumber(p.active_users),
        overview: parseOverview(p.overview),
        by_surface: Array.isArray(p.by_surface)
            ? p.by_surface.map((row) => {
                const r = row as Record<string, unknown>;
                return {
                    key: String(r.key ?? ''),
                    readers: asNumber(r.readers),
                    reads: asNumber(r.reads),
                    dwell_p50_sec: asNullableNumber(r.dwell_p50_sec),
                    dwell_avg_sec: asNullableNumber(r.dwell_avg_sec),
                    hop_pct: asNullableNumber(r.hop_pct),
                };
            })
            : [],
        by_channel: Array.isArray(p.by_channel)
            ? p.by_channel.map((row) => {
                const r = row as Record<string, unknown>;
                return {
                    key: String(r.key ?? ''),
                    readers: asNumber(r.readers),
                    reads: asNumber(r.reads),
                };
            })
            : [],
        by_type: Array.isArray(p.by_type)
            ? p.by_type.map((row) => {
                const r = row as Record<string, unknown>;
                return {
                    surface: String(r.surface ?? ''),
                    key: String(r.key ?? ''),
                    label: String(r.label ?? r.key ?? ''),
                    readers: asNumber(r.readers),
                    reads: asNumber(r.reads),
                    reads_pct: asNullableNumber(r.reads_pct),
                };
            })
            : [],
        top_articles: Array.isArray(p.top_articles)
            ? p.top_articles.map((row) => {
                const r = row as Record<string, unknown>;
                return {
                    surface: String(r.surface ?? ''),
                    slug: String(r.slug ?? ''),
                    title: String(r.title ?? r.slug ?? ''),
                    type_label: String(r.type_label ?? ''),
                    readers: asNumber(r.readers),
                    reads: asNumber(r.reads),
                    dwell_p50_sec: asNullableNumber(r.dwell_p50_sec),
                };
            })
            : [],
    };
}

export function useProductArticleAnalytics(days: number = 30) {
    return useQuery<ProductArticleAnalytics>({
        queryKey: ['admin', 'product-article-analytics', days],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('get_product_article_analytics', {
                p_days: days,
            });
            if (error) throw error;
            const parsed = parseProductArticleAnalytics(data);
            if (!parsed) throw new Error('Respuesta inválida de get_product_article_analytics');
            return parsed;
        },
        retry: 1,
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
    });
}

export const SURFACE_LABELS: Record<string, string> = {
    blog: 'Blog',
    atlas: 'Atlas',
};

export const CHANNEL_LABELS: Record<string, string> = {
    app: 'App',
    web: 'Web',
};

export const ATLAS_TYPE_LABELS: Record<string, string> = {
    dulce: 'Agua dulce',
    salada: 'Agua salada',
    both: 'Ambas',
    ambas: 'Ambas',
    sin_dato: 'Sin dato',
};
